import { Heading } from '@assembly-js/design-system';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { Formik, FormikConfig } from 'formik';
import { z } from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';

import Button from 'src/legacy/components/Button';
import { BaseTextField } from 'src/legacy/components/TextField';
import { HandleSaveValues } from 'src/routes/payments/hooks/usePaymentMethods';

interface AddCreditCardFormProps {
  onDismiss: () => void;
  onSave: (values: HandleSaveValues) => void;
}

const validationSchema = z.object({
  name: z.string().min(1, 'Cardholder name is required'),
  postalCode: z.string().optional(),
});

export const AddCreditCardForm = ({
  onDismiss,
  onSave,
}: AddCreditCardFormProps) => {
  const stripe = useStripe();
  const elements = useElements();

  const handleAddCard: FormikConfig<{
    name: string;
    postalCode: string;
  }>['onSubmit'] = async (values, { resetForm }) => {
    if (!stripe || !elements) {
      return;
    }

    const cardElement = elements.getElement(CardElement);

    if (!cardElement) {
      return;
    }

    try {
      const result = await stripe.createToken(cardElement, {
        name: values?.name,
        address_zip: values?.postalCode,
      });

      if (result.error) {
        console.error('Error registering card', { error: result.error });
      }

      if (result.token) {
        await onSave({
          ...values,
          sourceId: '',
          isPrimary: true, // make new card primary by default
          fields: {
            token: result.token,
          },
        });
        resetForm();
        onDismiss();
      }
    } catch (ex) {
      console.error('Error registering card', { ex });
    }
  };

  return (
    <Formik
      enableReinitialize
      initialValues={{ name: '', postalCode: '' }}
      validationSchema={toFormikValidationSchema(validationSchema)}
      onSubmit={handleAddCard}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        touched,
        values,
        isSubmitting,
      }) => {
        return (
          <form noValidate onSubmit={handleSubmit}>
            <div className="w-full mb-2">
              <Heading className="text-black-heading">Cardholder name</Heading>
              <BaseTextField
                name="name"
                variant="outlined"
                fullWidth
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.name}
                error={Boolean(touched.name && errors.name)}
                helperText={
                  touched && errors && touched.name && errors.name
                    ? errors.name
                    : ''
                }
              />
            </div>
            <div className="w-full mb-2">
              <Heading className="text-black-heading">Card</Heading>
              <CardElement
                className="border border-solid border-non-hover-border rounded-4 py-2 px-3"
                options={{
                  hidePostalCode: true,
                }}
              />
            </div>
            <div className="w-full mb-2">
              <div className="flex">
                <Heading className="text-black-heading">ZIP</Heading>
                &nbsp;
                <Heading className="text-gray-small">(Optional)</Heading>
              </div>
              <BaseTextField
                variant="outlined"
                name="postalCode"
                fullWidth
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.postalCode}
                error={Boolean(touched.postalCode && errors.postalCode)}
                helperText={
                  touched && errors && touched.postalCode && errors.postalCode
                    ? errors.postalCode
                    : ''
                }
              />
            </div>

            <div className="flex items-center gap-3">
              <Button
                htmlId="add-credit-card"
                variant="contained"
                color="primary"
                type="submit"
                isLoading={isSubmitting}
              >
                Add
              </Button>
              <Button
                htmlId="dismiss-add-credit-card"
                variant="contained"
                color="secondary"
                onClick={onDismiss}
                type="button"
              >
                Discard
              </Button>
            </div>
          </form>
        );
      }}
    </Formik>
  );
};
